import React, { useState } from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    ChevronRight as ChevronRightIcon,
    ExpandMore as ExpandMoreIcon,
    Visibility as VisibilityIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { useServiceOrdersDetailByStatus } from '../../../../../hooks/report/useServiceOrderReport';
import { STATUS_CONFIG } from '../utils/orderServiceReportConfig';
import { formatMoney, formatOrderCode, formatDate } from '../utils/formatters';


const MANIFEST_STATUS_CONFIG = {
    CREADO: { label: 'CREADO', color: '#90a4ae', text: '#fff' },
    PLANIFICADO: { label: 'PLANIFICADO', color: '#ffb300', text: '#000' },
    EN_PROCESO: { label: 'EN PROCESO', color: '#4caf50', text: '#fff' },
    COMPLETADO: { label: 'COMPLETADO', color: '#1e88e5', text: '#fff' },
    CANCELADO: { label: 'CANCELADO', color: '#e53935', text: '#fff' },
};

const TRAMO_CONFIG = {
    IDA: { label: 'SUBIDA', color: '#2e7d32' },
    VUELTA: { label: 'BAJADA', color: '#1565c0' },
};

const OrderServiceStatusDetailDialog = ({
    open,
    onClose,
    status,
    filters,
}) => {
    const navigate = useNavigate();
    const [expandedRows, setExpandedRows] = useState({});

    const {
        data: orders = [],
        isLoading,
        isFetching,
    } = useServiceOrdersDetailByStatus(status, filters, open);

    const statusInfo = STATUS_CONFIG[status] || {
        label: status,
        color: 'default',
    };

    const handleToggleExpand = (id) => {
        setExpandedRows((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xl"
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6" fontWeight={900}>
                            Órdenes en estado: {statusInfo.label}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            {orders.length} órdenes encontradas
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                            label={status}
                            color={statusInfo.color}
                            variant="outlined"
                        />

                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                {isLoading || isFetching ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : orders.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No hay órdenes para este estado con los filtros seleccionados.
                    </Typography>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    <TableCell><strong>Código</strong></TableCell>
                                    <TableCell><strong>Orden</strong></TableCell>
                                    <TableCell><strong>Proyecto</strong></TableCell>
                                    <TableCell><strong>Servicio</strong></TableCell>
                                    <TableCell><strong>Fecha</strong></TableCell>
                                    <TableCell align="right"><strong>Monto</strong></TableCell>
                                    <TableCell align="center"><strong>Manifiestos</strong></TableCell>
                                    <TableCell align="center"><strong>Valorización</strong></TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {orders.map((order) => {
                                    const orderId = order.ordsercabid;
                                    const isExpanded = Boolean(expandedRows[orderId]);
                                    const manifests = order.manifiestos_asociados || [];

                                    return (
                                        <React.Fragment key={orderId}>
                                            <TableRow hover>
                                                <TableCell width={48}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleToggleExpand(orderId)}
                                                    >
                                                        {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                                                    </IconButton>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography fontWeight={900}>
                                                        {formatOrderCode(orderId)}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={700}>
                                                        {order.ordsercabnom}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>{order.pronom}</TableCell>

                                                <TableCell>{order.serpronom}</TableCell>

                                                <TableCell>{formatDate(order.ordsercabfec)}</TableCell>

                                                <TableCell align="right">
                                                    <Typography fontWeight={900}>
                                                        {formatMoney(order.ordsercabpre)}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell align="center">
                                                    <Chip
                                                        size="small"
                                                        label={manifests.length}
                                                        color={manifests.length > 0 ? 'primary' : 'default'}
                                                        variant="outlined"
                                                    />
                                                </TableCell>

                                                <TableCell align="center">
                                                    <Chip
                                                        size="small"
                                                        label={order.valdetid ? 'Con valorización' : 'Sin valorización'}
                                                        color={order.valdetid ? 'success' : 'warning'}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                            </TableRow>

                                            <TableRow>
                                                <TableCell sx={{ py: 0 }} colSpan={9}>
                                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                        <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                                                            <Typography variant="subtitle2" fontWeight={900} gutterBottom>
                                                                Manifiestos asociados ({manifests.length})
                                                            </Typography>

                                                            {manifests.length === 0 ? (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    No hay manifiestos asociados a esta orden.
                                                                </Typography>
                                                            ) : (
                                                                <Table size="small">
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell><strong>Código</strong></TableCell>
                                                                            <TableCell><strong>Trayecto</strong></TableCell>
                                                                            <TableCell><strong>Vehículo</strong></TableCell>
                                                                            <TableCell><strong>Fecha salida</strong></TableCell>
                                                                            <TableCell><strong>Origen</strong></TableCell>
                                                                            <TableCell><strong>Destino</strong></TableCell>
                                                                            <TableCell align="right"><strong>Precio</strong></TableCell>
                                                                            <TableCell><strong>Estado</strong></TableCell>
                                                                            <TableCell align="center"><strong>Acción</strong></TableCell>
                                                                        </TableRow>
                                                                    </TableHead>

                                                                    <TableBody>
                                                                        {manifests.map((manifest) => {
                                                                            const tramo = TRAMO_CONFIG[manifest.ordsermantra];
                                                                            const manifestStatus =
                                                                                MANIFEST_STATUS_CONFIG[manifest.manest] || {
                                                                                    label: manifest.manest,
                                                                                    color: '#ccc',
                                                                                    text: '#000',
                                                                                };

                                                                            return (
                                                                                <TableRow key={`${manifest.ordsermanid}-${manifest.manid}`}>
                                                                                    <TableCell>
                                                                                        <Typography fontWeight={900}>
                                                                                            MN-{String(manifest.manid).padStart(6, '0')}
                                                                                        </Typography>
                                                                                    </TableCell>

                                                                                    <TableCell>
                                                                                        {tramo ? (
                                                                                            <Chip
                                                                                                size="small"
                                                                                                label={tramo.label}
                                                                                                variant="outlined"
                                                                                                sx={{
                                                                                                    borderColor: tramo.color,
                                                                                                    color: tramo.color,
                                                                                                    fontWeight: 800,
                                                                                                }}
                                                                                            />
                                                                                        ) : (
                                                                                            manifest.ordsermantra || '-'
                                                                                        )}
                                                                                    </TableCell>

                                                                                    <TableCell>
                                                                                        {manifest.vehdes || manifest.vehmat || '-'}
                                                                                    </TableCell>

                                                                                    <TableCell>{formatDate(manifest.manfec)}</TableCell>

                                                                                    <TableCell>{manifest.manori || '-'}</TableCell>

                                                                                    <TableCell>{manifest.mandes || '-'}</TableCell>

                                                                                    <TableCell align="right">
                                                                                        {formatMoney(manifest.ordsermanpre)}
                                                                                    </TableCell>

                                                                                    <TableCell>
                                                                                        <Chip
                                                                                            size="small"
                                                                                            label={manifestStatus.label}
                                                                                            sx={{
                                                                                                bgcolor: manifestStatus.color,
                                                                                                color: manifestStatus.text,
                                                                                                fontWeight: 800,
                                                                                            }}
                                                                                        />
                                                                                    </TableCell>

                                                                                    <TableCell align="center">
                                                                                        <Tooltip title="Ver manifiesto">
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                onClick={() =>
                                                                                                    navigate(`/administracion/manifiestos/${manifest.manid}/pasajeros`)
                                                                                                }
                                                                                            >
                                                                                                <VisibilityIcon sx={{ color: '#009688' }} fontSize="small" />
                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            );
                                                                        })}
                                                                    </TableBody>
                                                                </Table>
                                                            )}
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default OrderServiceStatusDetailDialog;